import React, { useState, useEffect } from 'react';
import { Box, Typography, Stack, FormControl, Select, MenuItem, InputLabel, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Language } from '@mui/icons-material';
import DashboardLayout from '../Layouts/DashboardLayout';
import TemplateGrid from './TemplateGrid';
import TemplatePreview from './TemplatePreview';
import LoadingPopup from './LoadingPopup';
import ProductServicePopup from './ProductServicePopup';
import GMBService from '../../services/GMBService';
import tokenManager from '../../auth/TokenManager';
import { getSession, getAutoToken } from '../../utils/authUtils';
import { API_BASE_URL } from '../../config/api';
import logger from '../../utils/logger';

// Main Container
const PageContainer = styled(Box)(({ theme }) => ({
  padding: '60px 80px 100px',
  backgroundColor: '#ffffff',
  minHeight: '100vh',
  [theme.breakpoints.down('lg')]: {
    padding: '50px 60px 80px'
  },
  [theme.breakpoints.down('md')]: {
    padding: '40px 40px 70px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '32px 24px 60px'
  }
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  maxWidth: '1400px',
  margin: '0 auto'
}));

// Header Section
const HeaderSection = styled(Box)(({ theme }) => ({
  marginBottom: '60px',
  [theme.breakpoints.down('md')]: {
    marginBottom: '48px'
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: '40px'
  }
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: 'clamp(36px, 5vw, 48px)',
  fontWeight: 400,
  color: '#1a1a1a',
  marginBottom: '12px',
  letterSpacing: '-0.5px'
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '16px',
  fontWeight: 400,
  color: '#666666',
  marginBottom: '32px'
}));

const ProfileSelector = styled(FormControl)(({ theme }) => ({
  minWidth: 320,
  maxWidth: 400,
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    fontSize: '14px',
    '& fieldset': {
      borderColor: '#d1d5db'
    },
    '&:hover fieldset': {
      borderColor: '#9ca3af'
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1a1a1a',
      borderWidth: '1px'
    }
  },
  '& .MuiInputLabel-root': {
    color: '#666666',
    fontSize: '14px',
    '&.Mui-focused': {
      color: '#1a1a1a'
    }
  },
  '& .MuiSelect-select': {
    padding: '12px 14px'
  },
  [theme.breakpoints.down('md')]: {
    minWidth: 280,
    maxWidth: '100%'
  }
}));

const FreeWebsite = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showTemplateGrid, setShowTemplateGrid] = useState(true);
  const [isCreatingWebsite, setIsCreatingWebsite] = useState(false);
  const [deploying, setDeploying] = useState(false);

  // Product/Service popup state
  const [showProductServicePopup, setShowProductServicePopup] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [userProductsServices, setUserProductsServices] = useState({ products: [], services: [] });
  const [savedProductsServices, setSavedProductsServices] = useState({ products: [], services: [] });
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedError, setSavedError] = useState(null);
  const [showUseSavedDialog, setShowUseSavedDialog] = useState(false);
  const [savedItemsForUse, setSavedItemsForUse] = useState({ products: [], services: [] });
  const [popupInitialItems, setPopupInitialItems] = useState({ products: [], services: [] });

  // Profile selection state
  const [availableProfiles, setAvailableProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [currentProfile, setCurrentProfile] = useState(null);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);

  // Load available profiles on component mount
  useEffect(() => {
    const loadProfiles = async () => {
      try {
        setProfilesLoading(true);

        const cachedProfiles = localStorage.getItem('availableGMBProfiles');
        const cachedOwner = localStorage.getItem('availableGMBProfilesOwner');
        // Determine current owner context (prefer session email; allow admin-view when impersonating)
        const session = getSession() || {};
        let currentOwner = (session?.email || session?.user?.email || '').toString().toLowerCase() || 'anonymous';
        try {
          const adminAccessDataRaw = sessionStorage.getItem('adminAccessData');
          if (adminAccessDataRaw) {
            const adminAccess = JSON.parse(adminAccessDataRaw);
            if (adminAccess?.targetUserId) currentOwner = `admin-view:${adminAccess.targetUserId}`;
          }
        } catch (_) { }

        if (cachedProfiles && cachedOwner && String(cachedOwner) === String(currentOwner)) {
          try {
            const profiles = JSON.parse(cachedProfiles);
            if (Array.isArray(profiles) && profiles.length > 0) {
              setAvailableProfiles(profiles);
              setSelectedProfileId(profiles[0].id);
              setCurrentProfile(profiles[0]);
              setProfilesLoading(false);
              return;
            }
          } catch (e) {
            logger.warn('[FreeWebsite] Failed to parse cached profiles:', e);
          }
        }

        const accessToken = localStorage.getItem('googleAccessToken');
        if (!accessToken) {
          throw new Error('Please visit Dashboard to connect your Google My Business account');
        }

        const accounts = await GMBService.getAccounts(accessToken);
        const allLocations = [];

        for (const acct of accounts) {
          if (!acct?.name) continue;
          const locs = await GMBService.getLocations(accessToken, acct.name);
          if (Array.isArray(locs) && locs.length) {
            locs.forEach(l => allLocations.push({ account: acct, loc: l }));
          }
        }

        const profiles = allLocations.map(({ account, loc }) => ({
          id: loc.name?.split('/').pop(),
          name: loc.title || loc.locationName || 'Unnamed Location',
          address: loc.storefrontAddress ?
            `${loc.storefrontAddress.addressLines?.join(', ') || ''}, ${loc.storefrontAddress.locality || ''}`.trim() :
            'Address not available',
          locationId: loc.name?.split('/').pop(),
          locationData: loc,
          accountData: account
        }));

        // Cache profiles scoped to the currentOwner so cached lists are only reused
        // when the signing-in context matches (avoids admin seeing other users' GMBs).
        try {
          localStorage.setItem('availableGMBProfiles', JSON.stringify(profiles));
          localStorage.setItem('availableGMBProfilesOwner', String(currentOwner));
        } catch (_) { }
        setAvailableProfiles(profiles);

        if (profiles.length > 0) {
          setSelectedProfileId(profiles[0].id);
          setCurrentProfile(profiles[0]);
        }
      } catch (err) {
        logger.error('Error loading profiles:', err);
        setProfileError(err.message);
      } finally {
        setProfilesLoading(false);
      }
    };

    loadProfiles();
  }, []);

  const handleProfileChange = (event) => {
    const selectedId = event.target.value;
    const profile = availableProfiles.find(p => p.id === selectedId);
    if (profile) {
      setSelectedProfileId(selectedId);
      setCurrentProfile(profile);
      setPreviewTemplate(null);
      setShowTemplateGrid(true);
      setSavedProductsServices({ products: [], services: [] });
    }
  };

  const fetchSavedProductsServices = async (profile) => {
    if (!profile?.id) return { products: [], services: [] };
    try {
      setSavedLoading(true);
      setSavedError(null);
      const token = getAutoToken();
      const res = await fetch(`${API_BASE_URL}/customer/auto-post/products-services/${profile.id}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw new Error(json?.message || 'Failed to load saved products/services');
      }
      const products = Array.isArray(json?.data?.products) ? json.data.products : [];
      const services = Array.isArray(json?.data?.services) ? json.data.services : [];
      return { products, services };
    } catch (e) {
      logger.warn('[FreeWebsite] Failed to fetch saved products/services:', e);
      setSavedError(e.message || 'Failed to load saved products/services');
      return { products: [], services: [] };
    } finally {
      setSavedLoading(false);
    }
  };

  const templates = [
    {
      id: 8,
      name: 'Premium Dark',
      image: '/images/template8.png',
      category: 'PREMIUM'
    },
    {
      id: 3,
      name: 'Modern SaaS',
      image: '/images/template3.jpg',
      category: 'BUSINESS'
    },
    {
      id: 2,
      name: 'Portfolio Minimal',
      image: '/images/template2.png',
      category: 'PORTFOLIO'
    },
    {
      id: 1,
      name: 'E-commerce Pro',
      image: '/images/template1.png',
      category: 'STORE'
    },
    {
      id: 5,
      name: 'Agency Bold',
      image: '/images/template5.png',
      category: 'AGENCY'
    },
  ];

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(selectedTemplate?.id === template.id ? null : template);
  };

  const handlePreview = (template) => {
    setPreviewTemplate(template);
    setShowTemplateGrid(false);
  };

  const createTemplateWithItems = async (template, { products, services }) => {
    setUserProductsServices({ products, services });

    if (!template) return;

    try {
      logger.log('[FreeWebsite] Using template with GMB data:', template);
      setIsCreatingWebsite(true);
      setSelectedTemplate({ ...template, loading: true });

      const { default: GMBWebsiteService } = await import('../../services/GMBWebsiteService');
      logger.log('[FreeWebsite] Fetching GMB data for template ID:', template.id);

      const result = await GMBWebsiteService.createWebsiteWithGMBData(template.id, { locationId: currentProfile?.id });

      if (result.success && result.templateData) {
        logger.log('[FreeWebsite] Successfully fetched GMB data');

        const combinedServices = [
          ...(result.templateData.services || []),
          ...services.map((s, idx) => ({
            id: `user-service-${s.id || idx}`,
            name: s.name,
            description: s.description,
            image: s.image
          })),
          ...products.map((p, idx) => ({
            id: `user-product-${p.id || idx}`,
            name: p.name,
            description: p.description,
            image: p.image
          }))
        ];

        const updatedTemplateData = {
          ...result.templateData,
          services: combinedServices,
          userProducts: products,
          userServices: services
        };

        const updatedTemplate = {
          ...template,
          realData: updatedTemplateData,
          gmbData: result.gmbData,
          loading: false,
          usesRealData: true,
          url: `${window.location.origin}/live-demo/${template.id}`
        };

        try {
          const templateResult = {
            template: updatedTemplate,
            templateData: updatedTemplateData,
            gmbData: result.gmbData,
            userProducts: products,
            userServices: services,
            createdAt: new Date().toISOString()
          };
          const previewKey = selectedProfileId ? `visibeen_website_${selectedProfileId}` : 'visibeen_website_default';
          localStorage.setItem(previewKey, JSON.stringify(templateResult));
          localStorage.setItem(`visibeen_template_${updatedTemplate.id}`, JSON.stringify(templateResult));
        } catch (_) { }

        setPreviewTemplate(updatedTemplate);
        setShowTemplateGrid(false);
        setIsCreatingWebsite(false);
      } else {
        logger.error('[FreeWebsite] Failed to fetch GMB data:', result.error);
        setIsCreatingWebsite(false);
        alert(`Could not fetch your Google My Business data: ${result.error}\n\nPlease ensure your GMB account is connected and try again.`);
        setSelectedTemplate(null);
      }
    } catch (error) {
      logger.error('[FreeWebsite] Error using template:', error);
      setIsCreatingWebsite(false);
      alert(`Error creating website: ${error.message}\n\nPlease ensure your GMB account is connected and try again.`);
      setSelectedTemplate(null);
    }
  };

  const handleUseTemplate = async (template) => {
    setPendingTemplate(template);

    const saved = await fetchSavedProductsServices(currentProfile);
    setSavedProductsServices(saved);

    if ((saved.products?.length || 0) > 0 || (saved.services?.length || 0) > 0) {
      setSavedItemsForUse(saved);
      setShowUseSavedDialog(true);
      return;
    }

    setPopupInitialItems({ products: [], services: [] });
    setShowProductServicePopup(true);
  };

  const handleProductServiceSubmit = async ({ products, services }) => {
    setShowProductServicePopup(false);

    try {
      const token = getAutoToken();
      const locationId = currentProfile?.id;
      const normalizedProducts = Array.isArray(products) ? products.map(p => p?.name).filter(Boolean) : [];
      const normalizedServices = Array.isArray(services) ? services.map(s => s?.name).filter(Boolean) : [];

      // Avoid overwriting saved items with empty payload (e.g., user clicked "Skip for now")
      if (locationId && (normalizedProducts.length > 0 || normalizedServices.length > 0)) {
        const saveRes = await fetch(`${API_BASE_URL}/customer/auto-post/products-services`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            locationId,
            products: normalizedProducts,
            services: normalizedServices
          })
        });

        const saveJson = await saveRes.json().catch(() => ({}));
        if (!saveRes.ok || saveJson?.success === false) {
          logger.warn('[FreeWebsite] Save products/services failed:', saveJson);
          setSavedError(saveJson?.message || 'Failed to save products/services');
        } else {
          setSavedProductsServices({
            products: saveJson?.data?.products || normalizedProducts,
            services: saveJson?.data?.services || normalizedServices
          });
        }
      }
    } catch (e) {
      logger.warn('[FreeWebsite] Failed to save products/services:', e);
      setSavedError(e.message || 'Failed to save products/services');
    }

    const template = pendingTemplate;
    if (!template) return;

    try {
      await createTemplateWithItems(template, { products, services });
    } catch (error) {
      logger.error('[FreeWebsite] Error using template:', error);
      setIsCreatingWebsite(false);
      alert(`Error creating website: ${error.message}\n\nPlease ensure your GMB account is connected and try again.`);
      setSelectedTemplate(null);
    }

    setPendingTemplate(null);
  };

  const handleViewLive = (template) => {
    setPreviewTemplate(null);
    window.open(`/live-demo/${template.id}`, '_blank');
  };

  const handleChangeTemplate = () => {
    setShowTemplateGrid(true);
  };

  const handleDeployToDirectory = async () => {
    try {
      if (!previewTemplate) return;
      setDeploying(true);
      const token = getAutoToken();
      const gmb = previewTemplate?.gmbData || {};
      const loc = gmb.location || {};
      const addr = loc.storefrontAddress || {};
      const title = loc.title || previewTemplate?.realData?.businessName || previewTemplate?.name || 'My Business';

      const categoriesField = loc?.categories || {};
      const gmbCat1 = loc?.primaryCategory?.displayName || loc?.primaryCategory?.name;
      const gmbCat2 = categoriesField?.primaryCategory?.displayName || categoriesField?.primaryCategory?.name;
      const gmbCat3 = Array.isArray(categoriesField?.additionalCategories) && (categoriesField.additionalCategories[0]?.displayName || categoriesField.additionalCategories[0]?.name);
      const gmbCat4 = Array.isArray(loc?.categories) && (loc.categories[0]?.displayName || loc.categories[0]?.name);
      const gmbPrimaryCategory = gmbCat1 || gmbCat2 || gmbCat3 || gmbCat4 || (gmb?.category?.displayName) || previewTemplate?.realData?.category || previewTemplate?.category || '';

      let addressString = Array.isArray(addr.addressLines) ? addr.addressLines.join(', ') : '';
      if (!addressString) addressString = previewTemplate?.realData?.address || '';
      if (!addressString && currentProfile?.address) addressString = currentProfile.address;

      let state = addr.administrativeArea || '';
      let city = addr.locality || '';
      let pin = addr.postalCode || '';

      if ((!state || !city) && addressString) {
        const parts = String(addressString).split(',').map(s => s.trim()).filter(Boolean);
        if (parts.length >= 2) {
          const last = parts[parts.length - 1];
          const secondLast = parts[parts.length - 2];
          const thirdLast = parts[parts.length - 3] || '';
          if (!pin && /\d{5,6}/.test(last)) pin = last.match(/\d{5,6}/)?.[0] || '';
          if (!state) state = secondLast;
          if (!city) city = thirdLast || parts[0];
        } else if (parts.length === 1) {
          if (!city) city = parts[0];
        }
      }

      const slugify = (value) => {
        if (!value) return '';
        return value.toString().toLowerCase().normalize('NFKD')
          .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      };

      // Initial guess for slugs (backend is source of truth)
      const stateSlug = slugify(state) || 'state';
      const citySlug = slugify(city) || 'city';
      const businessSlug = slugify(`${title || previewTemplate?.name || 'business'} ${city}`);
      const initialDirectoryUrl = `https://directory.visibeen.com/${stateSlug}/${citySlug}/${businessSlug}`;

      const templateData = {
        template: previewTemplate,
        templateData: previewTemplate?.realData || previewTemplate?.templateData || {},
        gmbData: previewTemplate?.gmbData || {},
        createdAt: new Date().toISOString()
      };

      const payload = {
        business_name: title,
        business_category: gmbPrimaryCategory,
        street_address: addressString,
        city,
        state,
        pin_code: pin,
        contact_number: loc.primaryPhone || '',
        website: initialDirectoryUrl,
        template_id: previewTemplate?.id || null,
        template_data: templateData
      };

      // 1. Initial Deploy
      const res = await fetch(`${API_BASE_URL}/directory/deploy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.success === false) {
        throw new Error(j?.message || 'Deployment failed');
      }

      // 2. Resolve Canonical URL from Backend Response
      let finalWebsiteUrl = initialDirectoryUrl;
      const result = j.result || {};

      if (result.stateSlug && result.citySlug && result.slug) {
        finalWebsiteUrl = `https://directory.visibeen.com/${result.stateSlug}/${result.citySlug}/${result.slug}`;
        logger.log('[Deploy] Canonical URL resolved:', finalWebsiteUrl);

        // 3. If canonical URL differs from initial guess, update directory listing again
        // This ensures the "Website" button in the directory points to the correct self-referential URL
        if (finalWebsiteUrl !== initialDirectoryUrl) {
          logger.log('[Deploy] Updating directory listing with canonical website URL...');
          payload.website = finalWebsiteUrl;
          await fetch(`${API_BASE_URL}/directory/deploy`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          }).catch(e => logger.warn('[Deploy] Failed to update canonical website in directory:', e));
        }
      }

      alert('Deployed to directory successfully.');

      // 4. Update GMB with the Final Canonical URL
      const locationName = loc?.name || '';
      if (finalWebsiteUrl && locationName) {
        try {
          await GMBService.updateBusinessProfile(locationName, { websiteUri: finalWebsiteUrl }, token);
          logger.log('[Deploy] Synced website to GMB:', finalWebsiteUrl);
        } catch (updateError) {
          logger.warn('[Deploy] Failed to sync website back to GMB:', updateError);
        }
      }
    } catch (e) {
      alert(`Failed to deploy: ${e.message}`);
    } finally {
      setDeploying(false);
    }
  };

  return (
    <DashboardLayout>
      <PageContainer>
        <ContentWrapper>
          <HeaderSection>
            <PageTitle>Templates</PageTitle>
            <PageSubtitle>Select a template to begin</PageSubtitle>

            {profileError && (
              <Alert
                severity="warning"
                sx={{ mb: 3, borderRadius: '6px' }}
              >
                {profileError}
              </Alert>
            )}

            {availableProfiles.length > 0 && !profilesLoading && (
              <ProfileSelector>
                <InputLabel>Select Business Profile</InputLabel>
                <Select
                  value={selectedProfileId}
                  onChange={handleProfileChange}
                  label="Select Business Profile"
                >
                  {availableProfiles.map((profile) => (
                    <MenuItem key={profile.id} value={profile.id}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '14px' }}>
                          {profile.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666666', fontSize: '12px' }}>
                          {profile.address}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </ProfileSelector>
            )}
          </HeaderSection>

          {showTemplateGrid && (
            <TemplateGrid
              templates={templates}
              selectedTemplate={selectedTemplate}
              onTemplateSelect={handleTemplateSelect}
              onPreview={handlePreview}
              onUseTemplate={handleUseTemplate}
            />
          )}
        </ContentWrapper>
      </PageContainer>

      <TemplatePreview
        template={previewTemplate}
        onClose={() => {
          setPreviewTemplate(null);
          setShowTemplateGrid(true);
        }}
        onUseTemplate={handleUseTemplate}
        onDeploy={handleDeployToDirectory}
        onViewLive={handleViewLive}
        onChangeTemplate={handleChangeTemplate}
        fullscreen={!showTemplateGrid}
      />

      <LoadingPopup open={isCreatingWebsite} />

      <Dialog
        open={showUseSavedDialog}
        onClose={() => setShowUseSavedDialog(false)}
        PaperProps={{ sx: { borderRadius: '12px', minWidth: 360 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: 18 }}>Use saved items?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
            We found saved products/services for this profile. Continue with them or edit.
          </Typography>
          {savedError && (
            <Alert severity="info" sx={{ mt: 1, borderRadius: '8px' }}>
              {savedError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setShowUseSavedDialog(false);
              setPopupInitialItems({
                products: (savedItemsForUse.products || []).map((p, i) => ({ id: `saved-product-${i}`, name: p, description: '', image: '/images/placeholder.jpg' })),
                services: (savedItemsForUse.services || []).map((s, i) => ({ id: `saved-service-${i}`, name: s, description: '', image: '/images/placeholder.jpg' }))
              });
              setShowProductServicePopup(true);
            }}
            sx={{ textTransform: 'none' }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setShowUseSavedDialog(false);
              await createTemplateWithItems(pendingTemplate, {
                products: (savedItemsForUse.products || []).map((p, i) => ({ id: `saved-product-${i}`, name: p, description: '', image: '/images/placeholder.jpg' })),
                services: (savedItemsForUse.services || []).map((s, i) => ({ id: `saved-service-${i}`, name: s, description: '', image: '/images/placeholder.jpg' }))
              });
            }}
            sx={{ textTransform: 'none' }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <ProductServicePopup
        open={showProductServicePopup}
        onClose={() => {
          setShowProductServicePopup(false);
          setPendingTemplate(null);
        }}
        onSubmit={handleProductServiceSubmit}
        template={pendingTemplate}
        profile={currentProfile}
        initialProducts={popupInitialItems.products}
        initialServices={popupInitialItems.services}
      />
    </DashboardLayout>
  );
};

export default FreeWebsite;
